import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { userService, USER_ID_KEY } from './services/userService';
import { UserProfile as UserProfileType, UserProgressItem, MASTERY_LABELS } from './types/user';

/** Maps the belt colour name (from the DB) to a CSS fill colour for the SVG. */
const BELT_FILL: Record<string, string> = {
  hvid:   '#FFFFFF',
  gul:    '#F5C518',
  orange: '#F97316',
  grøn:   '#16A34A',
  blå:    '#1D4ED8',
  brun:   '#78350F',
  sort:   '#171717',
};

/** Inline SVG of a judo belt — strokes stay black, only the fill changes. */
function BeltSvg({ colorName }: { colorName: string }) {
  const fill   = BELT_FILL[colorName.toLowerCase()] ?? '#CCCCCC';
  // White belt needs a visible stroke; all others use a subtle dark stroke
  const stroke = fill === '#FFFFFF' ? '#AAAAAA' : 'rgba(0,0,0,0.35)';
  return (
    <svg width="96" height="42" viewBox="0 0 448 195" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <rect fill={fill} x="14.99998" y="17.93671" width="422.99996" height="32.97966" stroke={stroke} strokeWidth="6"/>
      <rect fill={fill} x="106.45287" y="121.8013" width="210.29124" height="48"
            transform="matrix(0.385597 -0.604426 0.604426 0.385597 -13.7668 178.849)"
            stroke={stroke} strokeWidth="6"/>
      <rect fill={fill} x="270.45287" y="122.8013" width="210.29124" height="48"
            transform="matrix(-0.397678 -0.596547 0.596547 -0.397678 335.324 390.333)"
            stroke={stroke} strokeWidth="6"/>
      <ellipse fill={fill} cx="212.87793" cy="35.86044" rx="40.14915" ry="26.52712"
               stroke={stroke} strokeWidth="6"/>
    </svg>
  );
}

function UserProfile() {
  const navigate = useNavigate();
  const [user, setUser] = useState<UserProfileType | null>(null);
  const [progress, setProgress] = useState<UserProgressItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const userId = localStorage.getItem(USER_ID_KEY);
    if (!userId) { navigate('/login'); return; }

    Promise.all([
      userService.getUser(userId),
      userService.getProgress(userId),
    ]).then(([userData, progressData]) => {
        setUser(userData);
        setProgress(progressData);
      })
      .catch(() => setError('Kunne ikke hente profildata.'))
      .finally(() => setLoading(false));
  }, [navigate]);

  if (loading) return (
    <div className="App"><header className="App-header"><p>Indlæser...</p></header></div>
  );
  if (error) return (
    <div className="App"><header className="App-header"><p>{error}</p></header></div>
  );

  if (!user) return null;

  // XP progress towards the next belt (0–100 %)
  const beltProgressPct = (() => {
    if (!user.currentBelt || !user.nextBeltXpThreshold) return 100;
    const range = user.nextBeltXpThreshold - user.currentBelt.xpThreshold;
    const earned = user.totalXp - user.currentBelt.xpThreshold;
    return Math.min(Math.round((earned / range) * 100), 100);
  })();

  return (
    <div className="App">
      <header className="App-header">
        <div className="content-container">
          <button className="close-button" onClick={() => navigate('/')}>
            &times;
          </button>

          <h2>{user.username}</h2>

          {/* ── Belt card ── */}
          {user.currentBelt && (
            <div className="belt-card">
              <div className="belt-card-svg">
                <BeltSvg colorName={user.currentBelt.color} />
              </div>
              <div className="belt-card-info">
                <span className="belt-card-name">
                  {user.currentBelt.levelName}
                </span>
                {/* XP progress bar */}
                <div className="belt-xp-bar-track">
                  <div className="belt-xp-bar-fill" style={{ width: `${beltProgressPct}%` }} />
                </div>
                <span className="belt-xp-label">
                  {!user.nextBeltXpThreshold
                    ? 'Højeste bælteniveau nået 🏆'
                    : user.xpToNextBelt != null && user.xpToNextBelt <= 0
                      ? <span className="belt-xp-surplus">+{-user.xpToNextBelt} XP over kravet ✓</span>
                      : `${user.xpToNextBelt} XP til næste bælte`}
                </span>

                {/* Curriculum mastery requirement */}
                {user.nextBeltCurriculumTotal != null && user.nextBeltCurriculumTotal > 0 && (
                  <>
                    <div className="belt-xp-bar-track" style={{ marginTop: '0.5rem' }}>
                      <div
                        className="belt-xp-bar-fill belt-mastery-bar-fill"
                        style={{
                          width: `${Math.round(((user.nextBeltCurriculumMastered ?? 0) / user.nextBeltCurriculumTotal) * 100)}%`
                        }}
                      />
                    </div>
                    <span className="belt-xp-label">
                      {user.nextBeltCurriculumMastered ?? 0}/{user.nextBeltCurriculumTotal} teknikker på "Kan udenad"
                    </span>
                  </>
                )}
              </div>
            </div>
          )}

          {/* ── Stats row ── */}
          <div className="user-stats">
            <div className="stat-card">
              <span className="stat-value">{user.totalXp}</span>
              <span className="stat-label">Total XP</span>
            </div>
            <div className="stat-card">
              <span className="stat-value">{user.hasTrainedToday ? '✔' : '✘'}</span>
              <span className="stat-label">Trænet i dag</span>
            </div>
            {user.lastTrainingDate && (
              <div className="stat-card">
                <span className="stat-value">
                  {new Date(user.lastTrainingDate).toLocaleDateString('da-DK')}
                </span>
                <span className="stat-label">Sidste træning</span>
              </div>
            )}
          </div>

          {/* ── Mastery progress ── */}
          {progress.length > 0 ? (
            <div className="progress-section">
              <h3>Teknik fremskridt</h3>
              <ul className="data-list">
                {progress.map((p) => (
                  <li key={p.id} className="data-item progress-item">
                    <div className="mastery-bar-label">
                      <span className="curriculum-name">{p.curriculumName}</span>
                      <span className="mastery-label-badge">{MASTERY_LABELS[p.masteryLevel] ?? `Niveau ${p.masteryLevel}`}</span>
                    </div>
                    <div className="mastery-bar-label mastery-bar-sublabel">
                      {p.lastPracticed && (
                        <span className="last-practiced">
                          Sidst øvet: {new Date(p.lastPracticed).toLocaleDateString('da-DK')}
                        </span>
                      )}
                    </div>
                    <div className="mastery-bar-track">
                      <div
                        className="mastery-bar-fill"
                        style={{ width: `${(p.masteryLevel / 5) * 100}%` }}
                      />
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <p style={{ marginTop: '1.5rem', opacity: 0.7 }}>
              Ingen teknik fremskridt endnu — start en quiz!
            </p>
          )}

          <button
            className="quiz-link user-btn user-btn-secondary"
            style={{ marginTop: '2rem' }}
            onClick={() => { localStorage.removeItem(USER_ID_KEY); navigate('/login'); }}
          >
            Skift bruger
          </button>
        </div>
      </header>
    </div>
  );
}

export default UserProfile;
